import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useAnonymousTenancies } from "services/hooks/rentals";
import AnonymousTenanciesTable from "components/Tables/AnonymousTenanciesTable";
import * as XLSX from "xlsx";

const isBankMode = (paymentMode = "") => paymentMode.toUpperCase() === "BANK";

const isMobileMoneyMode = (paymentMode = "") => {
  const mode = paymentMode.toUpperCase();
  return mode.includes("MOBILE");
};

const formatCurrencyForExport = (amount, currency = "UGX") => {
  if (typeof amount !== "number") return `0 ${currency}`;
  return `${new Intl.NumberFormat("en-UG", { maximumFractionDigits: 0 }).format(amount)} ${currency}`;
};

const getPayoutInstructionForExport = (landlord = {}) => {
  if (!landlord.payment_mode) return "Not set";

  if (isBankMode(landlord.payment_mode)) {
    return `${landlord.bank_name || "No bank"} - ${landlord.account_number || "No account"}`;
  }

  return `${landlord.payment_mode} - ${landlord.mobile_number || "No mobile number"}`;
};

const AnonymousTenancies = () => {
  const roleFromState = useSelector((state) => state.role);
  const isAdminFlag = useSelector((state) => state.isAdmin);
  const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");
  const isManager = role === "manager";
  const [search, setSearch] = useState("");
  const [paymentInstructionFilter, setPaymentInstructionFilter] = useState("all");

  const { anonymousTenancies, count, isLoadingAnonymousTenancies, errorAnonymousTenancies } =
    useAnonymousTenancies();

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();
    return anonymousTenancies.filter((item) => {
      const paymentMode = item?.landlord?.payment_mode || "";

      if (paymentInstructionFilter === "bank" && !isBankMode(paymentMode)) return false;
      if (paymentInstructionFilter === "mobile_money" && !isMobileMoneyMode(paymentMode)) return false;

      if (!query) return true;

      const tenantName = `${item?.tenant?.first_name || ""} ${item?.tenant?.last_name || ""}`.toLowerCase();
      const tenantNumber = (item?.tenant?.tenant_number || "").toLowerCase();
      const propertyName = (item?.property?.name || "").toLowerCase();
      const unitName = (item?.unit?.name || "").toLowerCase();
      const landlordName = (item?.landlord?.name || "").toLowerCase();
      return (
        tenantName.includes(query) ||
        tenantNumber.includes(query) ||
        propertyName.includes(query) ||
        unitName.includes(query) ||
        landlordName.includes(query)
      );
    });
  }, [anonymousTenancies, search, paymentInstructionFilter]);

  const handleDownloadExcel = () => {
    const rows = [
      ["Anonymous Landlord Tenancies"],
      [],
      ["Filters"],
      ["Search", "Payment Instruction"],
      [search || "N/A", paymentInstructionFilter === "all" ? "All" : paymentInstructionFilter],
      [],
      [
        "Tenant Number",
        "Tenant Name",
        "Property",
        "Unit",
        "Occupancy Date",
        "Due Date",
        "Due Status",
        "Amount Due",
        "Amount Paid So Far",
        "Paid To Landlord",
        "Landlord Name",
        "Payment Mode",
        "Payout Instruction",
      ],
    ];

    filteredData.forEach((item) => {
      const tenantName = `${item?.tenant?.first_name || ""} ${item?.tenant?.last_name || ""}`.trim();
      rows.push([
        item?.tenant?.tenant_number || "",
        tenantName || "",
        item?.property?.name || "",
        item?.unit?.name || "",
        item?.occupancy?.date_added || "",
        item?.billing?.due_date || "",
        item?.billing?.due_status || "unknown",
        formatCurrencyForExport(item?.billing?.amount_due, item?.billing?.currency),
        formatCurrencyForExport(item?.billing?.amount_paid_so_far, item?.billing?.currency),
        formatCurrencyForExport(item?.landlord?.paid_to_landlord_total, item?.billing?.currency),
        item?.landlord?.name || "",
        item?.landlord?.payment_mode || "",
        getPayoutInstructionForExport(item?.landlord),
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Anonymous Tenancies");
    const fileName = `anonymous-tenancies-${paymentInstructionFilter}-${filteredData.length}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (!isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Property Managers can access Anonymous Tenancies.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
        Anonymous Landlord Tenancies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Active occupancies where landlord payout can only proceed after anonymous onboarding is completed.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Showing {filteredData.length} of {count} tenancy record(s).
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Search tenant, landlord, property or unit"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Payment Instruction</InputLabel>
            <Select
              label="Payment Instruction"
              value={paymentInstructionFilter}
              onChange={(event) => setPaymentInstructionFilter(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="mobile_money">Mobile Money</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3} display="flex" alignItems="center">
          <Button
            variant="contained"
            onClick={handleDownloadExcel}
            disabled={filteredData.length === 0}
            fullWidth
          >
            Download Excel
          </Button>
        </Grid>
      </Grid>

      <AnonymousTenanciesTable
        data={filteredData}
        loading={isLoadingAnonymousTenancies}
        error={errorAnonymousTenancies}
      />
    </Box>
  );
};

export default AnonymousTenancies;
